'use client';
import React, { useState, useCallback } from 'react';
import { ArrowLeft, CheckCircle, Circle, Clock, AlertTriangle, Lightbulb, RotateCcw, Play, Info,  } from 'lucide-react';
import type { Exercise } from './practiceData';
import type { PlacedComponent, Wire, WireColor, ComponentType } from '../../simulator-page/components/circuitTypes';
import { WIRE_COLOR_MAP } from '../../simulator-page/components/circuitTypes';
import { validateCircuit } from '../../simulator-page/components/circuitValidator';
import PracticeCanvas from './PracticeCanvas';

const DIFFICULTY_CONFIG = {
  beginner: { label: 'Beginner', bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  intermediate: { label: 'Intermediate', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  advanced: { label: 'Advanced', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
};

let instanceCounter = 100;
let wireCounter = 100;

interface Props {
  exercise: Exercise;
  onBack: () => void;
  onComplete: (id: string) => void;
  onFail: (id: string) => void;
  onReset: (id: string) => void;
}

export default function ExerciseDetail({ exercise, onBack, onComplete, onFail, onReset }: Props) {
  const [components, setComponents] = useState<PlacedComponent[]>([]);
  const [wires, setWires] = useState<Wire[]>([]);
  const [selectedColor, setSelectedColor] = useState<WireColor>('live');
  const [checkResult, setCheckResult] = useState<ReturnType<typeof validateCircuit> | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [simulationMode, setSimulationMode] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<Set<string>>(new Set());

  const diffCfg = DIFFICULTY_CONFIG[exercise.difficulty];

  const handleDropComponent = useCallback((type: ComponentType, x: number, y: number) => {
    instanceCounter += 1;
    const instanceId = `pcomp-${type}-${instanceCounter}`;
    const labelMap: Record<ComponentType, string> = {
      supply: 'Supply', lamp: 'Lamp', 'switch-one-way': 'SW1', 'switch-two-way': 'SW2',
      socket: 'Socket', 'junction-box': 'JB', mcb: 'MCB', rcbo: 'RCBO',
      fuse: 'Fuse', 'neutral-bar': 'N-Bar', 'earth-bar': 'E-Bar',
    };
    setComponents((prev) => [
      ...prev,
      {
        instanceId,
        type,
        x,
        y,
        label: `${labelMap[type]}${instanceCounter}`,
        switchState: type === 'switch-one-way' || type === 'switch-two-way' ? false : undefined,
      },
    ]);
    setCheckResult(null);
  }, []);

  const handleMoveComponent = useCallback((instanceId: string, x: number, y: number) => {
    setComponents((prev) => prev.map((c) => (c.instanceId === instanceId ? { ...c, x, y } : c)));
  }, []);

  const handleDeleteComponent = useCallback((instanceId: string) => {
    setComponents((prev) => prev.filter((c) => c.instanceId !== instanceId));
    setWires((prev) => prev.filter((w) => w.fromComponentId !== instanceId && w.toComponentId !== instanceId));
    setCheckResult(null);
  }, []);

  const handleAddWire = useCallback((wire: Wire) => {
    setWires((prev) => [...prev, wire]);
    setCheckResult(null);
  }, []);

  const handleDeleteWire = useCallback((wireId: string) => {
    setWires((prev) => prev.filter((w) => w.id !== wireId));
    setCheckResult(null);
  }, []);

  const handleToggleSwitch = useCallback((instanceId: string) => {
    setComponents((prev) =>
      prev.map((c) => (c.instanceId === instanceId ? { ...c, switchState: !c.switchState } : c))
    );
  }, []);

  const handleCheckAnswer = () => {
    const result = validateCircuit(components, wires);
    setCheckResult(result);
    if (result.status === 'correct') {
      onComplete(exercise.id);
    } else {
      onFail(exercise.id);
    }
  };

  const handleReset = () => {
    setComponents([]);
    setWires([]);
    setCheckResult(null);
    setShowHint(false);
    setSimulationMode(false);
    setSelectedComponentId(null);
    setCheckedSteps(new Set());
    instanceCounter = 100;
    wireCounter = 100;
    onReset(exercise.id);
  };

  const toggleStep = (stepId: string) => {
    setCheckedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const litLamps = new Set<string>();
  if (simulationMode && checkResult?.canSimulate) {
    for (const comp of components) {
      if (comp.type !== 'lamp') continue;
      const lampWires = wires.filter(
        (w) => w.fromComponentId === comp.instanceId || w.toComponentId === comp.instanceId
      );
      const hasLive = lampWires.some((w) => w.color === 'live');
      const hasNeutral = lampWires.some((w) => w.color === 'neutral');
      const switches = components.filter((c) => c.type === 'switch-one-way' || c.type === 'switch-two-way');
      const allSwitchesOn = switches.every((sw) => sw.switchState === true);
      if (hasLive && hasNeutral && (switches.length === 0 || allSwitchesOn)) {
        litLamps.add(comp.instanceId);
      }
    }
  }

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* Left: Instructions panel */}
      <div className="w-80 bg-white border-r border-border flex flex-col flex-shrink-0 overflow-hidden">
        {/* Back button */}
        <div className="px-4 py-3 border-b border-border">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 group"
          >
            <ArrowLeft size={15} className="group-hover:-translate-x-0.5 transition-transform duration-150" />
            Back to exercises
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Exercise header */}
          <div className="px-4 py-4 border-b border-border">
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${diffCfg.bg} ${diffCfg.text} ${diffCfg.border}`}>
                {diffCfg.label}
              </span>
              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock size={11} /> ~{exercise.estimatedTime} min
              </span>
            </div>
            <h2 className="text-base font-bold text-foreground mb-1">{exercise.title}</h2>
            <p className="text-xs text-muted-foreground leading-relaxed">{exercise.description}</p>
          </div>

          {/* Learning objectives */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Learning Objectives</p>
            <ul className="flex flex-col gap-1.5">
              {exercise.learningObjectives.map((obj, idx) => (
                <li key={`obj-${exercise.id}-${idx}`} className="flex items-start gap-2 text-xs text-slate-600">
                  <CheckCircle size={12} className="text-primary flex-shrink-0 mt-0.5" />
                  {obj}
                </li>
              ))}
            </ul>
          </div>

          {/* Required components */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">Required Components</p>
            <div className="flex flex-col gap-1.5">
              {exercise.requiredComponents.map((rc) => {
                const placed = components.filter((c) => c.type === rc.type).length;
                const satisfied = placed >= rc.count;
                return (
                  <div key={`req-${exercise.id}-${rc.type}`} className="flex items-center justify-between text-xs">
                    <span className={satisfied ? 'text-green-700 font-medium' : 'text-slate-600'}>
                      {rc.label}
                    </span>
                    <span className={`font-mono font-semibold ${satisfied ? 'text-green-600' : 'text-muted-foreground'}`}>
                      {placed}/{rc.count}
                      {satisfied && ' ✓'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Steps checklist */}
          <div className="px-4 py-3 border-b border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-2">
              Wiring Steps ({checkedSteps.size}/{exercise.steps.length})
            </p>
            <div className="flex flex-col gap-2">
              {exercise.steps.map((step, idx) => {
                const done = checkedSteps.has(step.id);
                return (
                  <button
                    key={step.id}
                    onClick={() => toggleStep(step.id)}
                    className={`flex items-start gap-2.5 text-left w-full group rounded-lg px-2 py-1.5 transition-colors duration-150
                      ${done ? 'bg-green-50' : 'hover:bg-slate-50'}`}
                  >
                    {done ? (
                      <CheckCircle size={14} className="text-green-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <Circle size={14} className="text-slate-300 flex-shrink-0 mt-0.5 group-hover:text-slate-400" />
                    )}
                    <span className={`text-xs leading-relaxed ${done ? 'text-green-700 line-through' : 'text-slate-600'}`}>
                      <span className="font-semibold text-muted-foreground mr-1">{idx + 1}.</span>
                      {step.instruction}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Regulation */}
          <div className="px-4 py-3 border-b border-border">
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3">
              <Info size={13} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">Relevant Regulation</p>
                <p className="text-xs text-blue-600 leading-relaxed">{exercise.regulation}</p>
              </div>
            </div>
          </div>

          {/* Hint */}
          <div className="px-4 py-3">
            <button
              onClick={() => setShowHint((v) => !v)}
              className="flex items-center gap-2 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors duration-150 mb-2"
            >
              <Lightbulb size={13} />
              {showHint ? 'Hide Hint' : 'Show Hint'}
            </button>
            {showHint && (
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 animate-fade-in">
                <p className="text-xs text-amber-700 leading-relaxed">{exercise.hint}</p>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-4 py-3 border-t border-border flex flex-col gap-2">
          <button
            onClick={handleCheckAnswer}
            className="flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-all duration-150 active:scale-95"
          >
            <Play size={14} /> Check My Answer
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => setSimulationMode((v) => !v)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border transition-all duration-150
                ${simulationMode
                  ? 'bg-amber-50 border-amber-300 text-amber-700' :'bg-white border-border text-muted-foreground hover:bg-muted'
                }`}
            >
              {simulationMode ? '⏸ Stop Sim' : '▶ Simulate'}
            </button>
            <button
              onClick={handleReset}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-border bg-white text-muted-foreground hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all duration-150"
            >
              <RotateCcw size={12} /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Right: Canvas + result */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Wire colour selector bar */}
        <div className="bg-white border-b border-border px-4 py-2 flex items-center gap-3 flex-wrap">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">Wire Colour:</span>
          {(Object.keys(WIRE_COLOR_MAP) as WireColor[]).map((color) => {
            const cfg = WIRE_COLOR_MAP[color];
            const isSelected = selectedColor === color;
            return (
              <button
                key={`prac-wire-${color}`}
                onClick={() => setSelectedColor(color)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all duration-150
                  ${isSelected ? 'border-current shadow-sm bg-slate-50' : 'border-border hover:bg-muted'}`}
                style={isSelected ? { color: cfg.hex, borderColor: cfg.hex } : {}}
              >
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.hex }} />
                {cfg.label}
              </button>
            );
          })}
          <div className="flex-1" />
          <span className="text-xs text-muted-foreground font-mono">
            {components.length} components · {wires.length} wires
          </span>
        </div>

        {/* Check result banner */}
        {checkResult && (
          <div className={`px-4 py-3 border-b flex items-start gap-3 animate-fade-in
            ${checkResult.status === 'correct' ?'bg-green-50 border-green-200'
              : checkResult.status === 'unsafe'|| checkResult.status === 'short-circuit' ?'bg-red-50 border-red-200' :'bg-amber-50 border-amber-200'
            }`}
          >
            {checkResult.status === 'correct' ? (
              <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-bold mb-0.5 ${checkResult.status === 'correct' ? 'text-green-700' : 'text-amber-700'}`}>
                {checkResult.status === 'correct' ?'✓ Correct! Circuit is safely and correctly wired.'
                  : `Issues found: ${checkResult.faults.length} fault${checkResult.faults.length > 1 ? 's' : ''} detected`}
              </p>
              {checkResult.faults.slice(0, 2).map((f) => (
                <p key={`prac-fault-${f.code}`} className="text-xs text-slate-600 leading-relaxed truncate">
                  {f.title}: {f.message.slice(0, 100)}{f.message.length > 100 ? '…' : ''}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Canvas */}
        <PracticeCanvas
          components={components}
          wires={wires}
          selectedColor={selectedColor}
          simulationMode={simulationMode}
          litLamps={litLamps}
          selectedComponentId={selectedComponentId}
          onSelectComponent={setSelectedComponentId}
          onDropComponent={handleDropComponent}
          onMoveComponent={handleMoveComponent}
          onDeleteComponent={handleDeleteComponent}
          onAddWire={handleAddWire}
          onDeleteWire={handleDeleteWire}
          onToggleSwitch={handleToggleSwitch}
          wireCounterRef={{ current: wireCounter }}
        />
      </div>
    </div>
  );
}